import { KeyboardTabOutlined, KeyboardBackspaceOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';

import {
  OTC_TRADER_TENOR_LIST,
  OTC_TRADER_DELTA_LIST,
} from '../../../../../constants/reports/reports-filters';
import { SurfaceMarkingContext } from '../../../../../providers/otc-trader/surface-marking-provider';
import FilteredList from './filterable-list';
import { OtcTraderSelect } from './otc-trader-select';
import { VolatilitySufaceMarkingSidebarSelect } from './volatility-suface-marking-sidebar-select';
import { VolatilitySurfaceMarkingSidebarDropdown } from '../../../../../pages/otc-trader/components/volatility-surface-marking-sidebar-dropdown';
import './styles.scss';

const SPOT_SOURCE_VALUES = [
  { name: 'Deribit', value: 'deribit' },
  { name: 'OKX', value: 'okx' },
  { name: 'Binance', value: 'binance_futures' },
  { name: 'Mexc', value: 'mexc' },
];

type VolatilitySufaceMarkingSidebarProps = {
  isVolatilitySufaceMarkingSidebarOpen: boolean;
  setIsVolatilitySufaceMarkingSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const SelectionDropdown = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <VolatilitySufaceMarkingSidebarSelect label={label}>
    {children}
  </VolatilitySufaceMarkingSidebarSelect>
);

export const VolatilitySufaceMarkingSidebar = ({
  isVolatilitySufaceMarkingSidebarOpen,
  setIsVolatilitySufaceMarkingSidebarOpen,
}: VolatilitySufaceMarkingSidebarProps) => {
  const {
    volatilityOptions,
    setVolatilityOptions,
    volatilityData,
    surfaceLoading,
    isMarketPairTokenLoading,
    getListTokens,
    getAllListRateSymbols,
    options: { tokenPairOption, surfaceOptions, spotSourceOptions, deltaOption, tenorOption },
  } = useContext(SurfaceMarkingContext);

  const [shouldResetSelected, setShouldResetSelected] = useState(false);

  const toggleSidebar = useCallback(() => {
    setIsVolatilitySufaceMarkingSidebarOpen((prevState) => !prevState);
  }, [setIsVolatilitySufaceMarkingSidebarOpen]);

  const handleDeltaChange = useCallback(
    (value: any) => {
      deltaOption.handleChange(value[0]?.length > 0 ? value[0] : value);
    },
    [deltaOption],
  );

  useEffect(() => {
    getListTokens();
  }, [getListTokens]);

  useEffect(() => {
    if (!volatilityOptions.tokenPair || surfaceLoading || isMarketPairTokenLoading) return;

    setShouldResetSelected(true);
    setVolatilityOptions((state) => ({ ...state, surface: undefined }));
    getAllListRateSymbols(volatilityOptions.tokenPair);
  }, [volatilityOptions.tokenPair]);

  if (isMarketPairTokenLoading) return <div className="volatility-sidebar-loading">Loading...</div>;

  const { delta } = volatilityOptions;

  return (
    <div className="volatility-suface-marking-sidebar-container">
      <div
        className={`volatility-suface-marking-sidebar-button-change-sidebar ${
          isVolatilitySufaceMarkingSidebarOpen
            ? 'volatility-suface-marking-sidebar-button-change-sidebar-open'
            : 'volatility-suface-marking-sidebar-button-change-sidebar-close'
        }`}
      >
        <Button onClick={toggleSidebar}>
          {isVolatilitySufaceMarkingSidebarOpen ? (
            <KeyboardBackspaceOutlined />
          ) : (
            <KeyboardTabOutlined />
          )}
        </Button>
      </div>

      {isVolatilitySufaceMarkingSidebarOpen && (
        <div className="volatility-suface-marking-sidebar-content">
          <SelectionDropdown label="Token Pair">
            <OtcTraderSelect
              values={volatilityData?.marketPairTokens as any}
              defaultValue={{
                name: volatilityOptions.tokenPair || 'Token Pair',
                value: volatilityOptions.tokenPair || '',
              }}
              onChange={({ value }: { value: any }) => tokenPairOption.handleChange(value)}
              isSearchField
              className="volatility-suface-marking-sidebar-content-select-input"
            />
          </SelectionDropdown>

          <SelectionDropdown label="Surface">
            <FilteredList
              values={volatilityData.listUserVolSurfacesBySymbol}
              onChange={(value: any) => {
                setShouldResetSelected(false);
                surfaceOptions.handleChange(value);
              }}
              className="volatility-suface-marking-sidebar-content-select-input"
              placeholder="Surface"
              resetSelected={shouldResetSelected}
            />
          </SelectionDropdown>

          <SelectionDropdown label="Spot Source">
            <OtcTraderSelect
              values={SPOT_SOURCE_VALUES}
              defaultValue={
                SPOT_SOURCE_VALUES.find(
                  (source) => source.value === volatilityOptions.spotSource,
                ) || SPOT_SOURCE_VALUES[0]
              }
              onChange={({ value }: { value: any }) => spotSourceOptions.handleChange(value)}
              className="volatility-suface-marking-sidebar-content-select-input"
            />
          </SelectionDropdown>

          <SelectionDropdown label="Parameters">
            <div className="volatility-suface-marking-sidebar-content-parameters">
              <VolatilitySurfaceMarkingSidebarDropdown
                itemTitle="Delta"
                items={OTC_TRADER_DELTA_LIST}
                defaultValue={
                  OTC_TRADER_DELTA_LIST.find((deltaList) => deltaList.id === delta?.id) ||
                  OTC_TRADER_DELTA_LIST[0]
                }
                handleChange={handleDeltaChange}
                selectedItems={deltaOption.selectedItems}
                classNames="volatility-suface-marking-sidebar-content-parameters-delta"
                isRadio
                canUpdate
              />
              <VolatilitySurfaceMarkingSidebarDropdown
                itemTitle="Tenor"
                items={OTC_TRADER_TENOR_LIST}
                defaultValue={
                  volatilityOptions.tenor.length > 0
                    ? OTC_TRADER_TENOR_LIST.filter((tenorList) =>
                        volatilityOptions.tenor.includes(tenorList.value),
                      )
                    : OTC_TRADER_TENOR_LIST.filter((tenorList) =>
                        ['1W', '2W'].includes(tenorList.value),
                      )
                }
                handleChange={tenorOption.handleChange}
                selectedItems={tenorOption.selectedItems}
                classNames="volatility-suface-marking-sidebar-content-parameters-tenor"
                canUpdate
              />
            </div>
          </SelectionDropdown>
        </div>
      )}
    </div>
  );
};
